import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { LegalEntityPermissions } from '../../features/auth/models/business-user-roles.model';
import { getSavedBusinessId } from '../../features/legal-entities/selectors/legal-entities-list.selectors';
import { EMPTY_GUID } from '../../shared/constants';
import { SessionStorageIds } from '../../shared/enums';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private permissions$ = new BehaviorSubject<string[]>([]);
  private legalEntityPermissions$ = new BehaviorSubject<LegalEntityPermissions[]>([]);
  private currentLegalEntityId$ = new BehaviorSubject<string | null>(null);

  constructor(private router: Router, private store: Store) {
    this.refreshFromSession();

    // Re-read permissions from session on every navigation so nav items re-evaluate
    // after the current legal entity / businessId changes (e.g. entering an LE-scoped screen).
    this.router.events
      .pipe(filter((ev): ev is NavigationEnd => ev instanceof NavigationEnd))
      .subscribe(() => this.refreshFromSession());

    // Re-read permissions when the user switches legal entity via the popup (no navigation occurs).
    this.store
      .select(getSavedBusinessId)
      .pipe(
        filter((id): id is string => !!id),
        distinctUntilChanged()
      )
      .subscribe(() => this.refreshFromSession());
  }

  /**
   * Check if user has a specific permission
   * @param permissionCode Permission code (e.g., 'business.view.own')
   * @param requireLegalEntityContext If true, checks permissions scoped to current legal entity
   */
  hasPermission(permissionCode: string, requireLegalEntityContext: boolean = false): Observable<boolean> {
    return this.hasAnyPermission([permissionCode], requireLegalEntityContext);
  }

  /**
   * Check if user has any of the specified permissions
   */
  hasAnyPermission(permissionCodes: string[], requireLegalEntityContext: boolean = false): Observable<boolean> {
    return combineLatest([
      this.permissions$,
      this.legalEntityPermissions$,
      this.currentLegalEntityId$
    ]).pipe(
      map(([permissions, legalEntityPermissions, currentLegalEntityId]) => {
        const scopedPermissions = this.getScopedPermissions(
          permissions || [],
          legalEntityPermissions,
          currentLegalEntityId,
          requireLegalEntityContext
        );
        if (!scopedPermissions || scopedPermissions.length === 0) {
          return false;
        }

        // Check for SuperUser (all permissions)
        if (scopedPermissions.includes('*')) {
          return true;
        }

        // Check if any permission matches
        return permissionCodes.some(code => scopedPermissions.includes(code));
      })
    );
  }

  /**
   * Get current legal entity ID from sessionStorage
   */
  getCurrentLegalEntityId(): string | null {
    return sessionStorage.getItem('businessId');
  }

  refreshFromSession(): void {
    this.permissions$.next(this.readFromSession<string[]>(SessionStorageIds.AUTH_PERMISSIONS, []));
    this.legalEntityPermissions$.next(
      this.readFromSession<LegalEntityPermissions[]>(SessionStorageIds.AUTH_LEGAL_ENTITY_PERMISSIONS, [])
    );
    this.currentLegalEntityId$.next(
      sessionStorage.getItem(SessionStorageIds.AUTH_CURRENT_LEGAL_ENTITY_ID) || sessionStorage.getItem(SessionStorageIds.BUSINESS_ID)
    );
  }

  /**
   * Set the current legal entity and sync AUTH_PERMISSIONS from legal-entity-scoped permissions.
   * Call this when entering a screen (e.g. product-details) that should use a specific business's permissions.
   */
  setCurrentLegalEntityAndSyncPermissions(legalEntityId: string): void {
    const legalEntityPermissions = this.readFromSession<LegalEntityPermissions[]>(
      SessionStorageIds.AUTH_LEGAL_ENTITY_PERMISSIONS,
      []
    );
    const entity = legalEntityPermissions?.find(e => e.legalEntityId === legalEntityId);
    sessionStorage.setItem(SessionStorageIds.AUTH_PERMISSIONS, JSON.stringify(entity?.permissions ?? []));
    sessionStorage.setItem(SessionStorageIds.AUTH_CURRENT_LEGAL_ENTITY_ID, legalEntityId);
    sessionStorage.setItem(SessionStorageIds.BUSINESS_ID, legalEntityId);
    this.refreshFromSession();
  }

  /**
   * Deny-by-default when no valid legal entity can be resolved for the screen being entered
   * (e.g. a record with a missing/blank businessId). Prevents a stale, unrelated legal entity's
   * AUTH_PERMISSIONS from leaking into this screen's permission checks.
   */
  clearCurrentLegalEntityPermissions(): void {
    sessionStorage.setItem(SessionStorageIds.AUTH_PERMISSIONS, JSON.stringify([]));
    sessionStorage.removeItem(SessionStorageIds.AUTH_CURRENT_LEGAL_ENTITY_ID);
    this.refreshFromSession();
  }

  private readFromSession<T>(key: string, fallback: T): T {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return fallback;
    }
    try {
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  }

  private getScopedPermissions(
    permissions: string[],
    legalEntityPermissions: LegalEntityPermissions[] | undefined,
    currentLegalEntityId: string | null | undefined,
    requireLegalEntityContext: boolean
  ): string[] {
    if (!requireLegalEntityContext) {
      return permissions;
    }

    const businessId = currentLegalEntityId || this.getCurrentLegalEntityId();
    if (!businessId) {
      return [];
    }
    const scoped = legalEntityPermissions?.find(entity => entity.legalEntityId === businessId);
    return scoped?.permissions || [];
  }
}
